import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles,
  Paintbrush,
  Gem,
  Flame,
  Cookie,
  Boxes,
  Lightbulb,
  GraduationCap,
  DollarSign,
  Users,
  TrendingUp,
  Handshake,
  Star,
  Download,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Swap this to the finished 2026 PDF when it's ready — the download button
// and hero secondary CTA both consume this single URL.
const GUIDELINES_PDF_URL = null; // e.g. "https://.../2026-ams-kids-guidelines.pdf"
const GUIDELINES_YEAR = "2026";

const PRODUCTS = [
  { icon: Lightbulb, label: "Inventions", tint: "#E8D2F4" },
  { icon: Paintbrush, label: "Original Artwork", tint: "#F4DFC8" },
  { icon: Gem, label: "Jewelry", tint: "#F4C8D9" },
  { icon: Flame, label: "Candles", tint: "#F7EBB2" },
  { icon: Cookie, label: "Baked Goods", tint: "#EFD1A6" },
  { icon: Boxes, label: "3D Printed Characters", tint: "#CFE5D9" },
];

const SKILLS = [
  {
    icon: Handshake,
    title: "Working with customers",
    body: "Greeting a stranger, describing what you made, and closing a sale — soft skills that unlock every career after this one.",
  },
  {
    icon: DollarSign,
    title: "Earning real money",
    body: "Young CEOs keep what they earn. Every dollar teaches them that their craft has real value in the real world.",
  },
  {
    icon: TrendingUp,
    title: "Profit and margins",
    body: "We teach the math behind the price tag: cost of goods, time, overhead — and what a healthy margin actually looks like.",
  },
  {
    icon: Users,
    title: "Craftsmanship alongside adults",
    body: "Young CEOs work the same market floor as seasoned makers — a mentor is always a booth away.",
  },
];

const HOW_IT_WORKS = [
  {
    n: "01",
    title: "Apply as a Young CEO",
    body: "Kids age 8–17 (with a parent's co-signature) tell us what they make and why they love it.",
  },
  {
    n: "02",
    title: "Prep with our starter kit",
    body: "A short workshop covers pricing, display, cash handling, and how to talk to shoppers — plus a signage template.",
  },
  {
    n: "03",
    title: "Sell at an AMS event",
    body: "Young CEOs get their own booth at select markets in the \"AMS Kids\" Department. Same shoppers, same opportunity as all our other makers.",
  },
  {
    n: "04",
    title: "Debrief and grow",
    body: "After every event we sit with each Young CEO and review what sold, what didn't, and what to try next time.",
  },
];

export default function AMSKidsPage() {
  const [form, setForm] = useState({
    parent_name: "",
    kid_name: "",
    email: "",
    phone: "",
    age: "",
    product_description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.kid_name.trim() || !form.email.trim() || !form.product_description.trim()) {
      toast.error("Please fill in the parent name, Young CEO's name, email, and what they make.");
      return;
    }
    setSubmitting(true);
    const message = [
      `YOUNG CEO: ${form.kid_name}${form.age ? ` (age ${form.age})` : ""}`,
      `PARENT / GUARDIAN: ${form.parent_name}`,
      "",
      "WHAT THEY MAKE:",
      form.product_description,
    ].join("\n");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.parent_name,
          email: form.email,
          phone: form.phone || null,
          subject: `AMS Kids application — ${form.kid_name}`,
          message,
          event_context: "ams-kids-young-ceos",
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setSubmitted(true);
      toast.success("Application received — we'll be in touch about the next Young CEOs cohort.");
    } catch {
      toast.error("Something went wrong. Please try again or email info@artisanmarketandstudio.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="editorial min-h-screen" data-testid="ams-kids-page">
      <Helmet>
        <title>AMS Kids — Young CEOs | Artisan Market &amp; Studio</title>
        <meta
          name="description"
          content="AMS Kids is a young-entrepreneur program for makers age 8–17. Young CEOs sell their handmade goods at AMS events and learn pricing, profit, and customer skills alongside seasoned artisans."
        />
      </Helmet>

      {/* Hero — Contact-page style: real <img> so the photo is fully visible,
          bottom-weighted forest scrim so text stays crisp, feather into cream. */}
      <section className="relative overflow-hidden" data-testid="ams-kids-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1686942485101-2f975378395f?w=1800&q=80"
            alt="Kids holding colorful handmade slime, showing off their craft"
            className="w-full h-full object-cover"
            data-testid="ams-kids-hero-image"
          />
          {/* Bottom-weighted scrim — image reads at top, text is crisp at bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E3227]/95 from-0% via-[#1E3227]/70 via-40% to-[#1E3227]/20 to-95%" />
          {/* Feather into cream */}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[color:var(--ed-cream)]" />
        </div>

        <div
          className="relative max-w-5xl mx-auto px-6 pt-28 pb-20 md:pt-36 md:pb-28 min-h-[58vh] flex flex-col justify-end text-[#FDF7EA]"
          style={{ textShadow: "0 2px 16px rgba(0,0,0,0.7), 0 1px 3px rgba(0,0,0,0.5)" }}
        >
          <Link
            to="/university"
            className="inline-flex items-center gap-2 text-sm font-body opacity-85 hover:opacity-100 mb-8 self-start"
            data-testid="ams-kids-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to AMS University
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[color:var(--ed-gold)]"
              data-testid="ams-kids-kicker"
            >
              <Star className="w-3.5 h-3.5" />
              New for 2026 · A branch of AMS University
            </span>
            <h1
              className="mt-4 text-5xl md:text-7xl font-editorial leading-[1.02] max-w-3xl"
              data-testid="ams-kids-title"
            >
              AMS Kids.
              <br />
              <span className="text-[color:var(--ed-gold)]">Young CEOs</span> in the making.
            </h1>
            <p className="mt-5 font-body text-lg md:text-xl max-w-2xl text-[#FDF7EA]/90">
              AMS Kids is a young-entrepreneur program for makers age 8–17. Young CEOs
              sell their handmade goods at AMS events under our banner — and learn the
              craft of running a real business alongside seasoned artisans.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#apply-form" className="btn-primary" data-testid="ams-kids-cta-primary">
                Apply your Young CEO
                <ArrowRight className="w-4 h-4" />
              </a>
              {GUIDELINES_PDF_URL && (
                <a
                  href={GUIDELINES_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#FDF7EA]/40 text-[#FDF7EA] font-body text-sm hover:bg-[#FDF7EA]/10 transition-colors"
                  data-testid="ams-kids-guidelines-hero"
                >
                  <Download className="w-4 h-4" />
                  Read the {GUIDELINES_YEAR} guidelines
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Young CEOs sell — product grid with iconography */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-12" data-testid="ams-kids-products">
        <div className="flex items-center mb-6">
          <span className="rule" />
          <span className="kicker">Learning the joy of making</span>
        </div>
        <h2 className="font-editorial text-4xl md:text-5xl leading-tight text-[color:var(--ed-forest-dark)] max-w-3xl">
          From idea to the first sale, a booth of their very own.
        </h2>
        <p className="mt-4 font-body text-base text-[color:var(--ed-muted)] max-w-2xl">
          AMS Kids is happy to help young makers channel their inner CEO! If they
          have made it themselves and they&apos;re proud of it, we want to give
          them a platform to sell it and learn how to grow their business.
        </p>
        <p
          className="mt-4 font-editorial text-xl md:text-2xl italic text-[color:var(--ed-terracotta)] max-w-2xl leading-snug"
          data-testid="ams-kids-journey-quote"
        >
          Every journey starts with a single step and we are so excited to be a part of <em className="not-italic font-semibold">YOUR</em> journey!
        </p>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.label}
                className="card-warm p-6 text-center hover:-translate-y-0.5 transition-transform"
                data-testid={`ams-kids-product-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                  style={{ backgroundColor: p.tint, color: "#5A3D2A" }}
                >
                  <Icon className="w-8 h-8" />
                </div>
                <div className="font-editorial text-xl text-[color:var(--ed-forest-dark)]">
                  {p.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Skills — the whole point */}
      <section className="bg-[color:var(--ed-cream)]/60 border-y border-[color:var(--ed-forest)]/10">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex items-center mb-6">
            <span className="rule" />
            <span className="kicker">What they learn</span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl leading-tight text-[color:var(--ed-forest-dark)] max-w-3xl">
            Skills that outlast the booth.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="ams-kids-skills">
            {SKILLS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-[color:var(--ed-forest)]/10"
                  data-testid={`ams-kids-skill-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-11 h-11 rounded-full flex-shrink-0"
                    style={{ backgroundColor: "rgba(200, 107, 62, 0.14)", color: "#8F3E22" }}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-editorial text-2xl text-[color:var(--ed-forest-dark)]">
                      {s.title}
                    </h3>
                    <p className="mt-2 font-body text-sm text-[color:var(--ed-muted)]">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-center mb-6">
          <span className="rule" />
          <span className="kicker">How it works</span>
        </div>
        <h2 className="font-editorial text-4xl md:text-5xl leading-tight text-[color:var(--ed-forest-dark)] max-w-3xl">
          From application to first sale, in four steps.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="ams-kids-steps">
          {HOW_IT_WORKS.map((step) => (
            <div key={step.n} className="card-warm p-6" data-testid={`ams-kids-step-${step.n}`}>
              <div className="font-editorial text-4xl text-[color:var(--ed-terracotta)]">
                {step.n}
              </div>
              <h3 className="mt-4 font-editorial text-2xl text-[color:var(--ed-forest-dark)]">
                {step.title}
              </h3>
              <p className="mt-2 font-body text-sm text-[color:var(--ed-muted)] leading-relaxed">
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Guidelines PDF slot */}
      <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="ams-kids-guidelines-slot">
        <div className="card-warm p-8 md:p-10 bg-[color:var(--ed-forest-dark)] text-[#FDF7EA]">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <div className="text-xs font-body uppercase tracking-widest text-[color:var(--ed-gold)]">
                Young CEO handbook · {GUIDELINES_YEAR}
              </div>
              <h3 className="mt-2 font-editorial text-3xl md:text-4xl">
                Download the AMS Kids guidelines.
              </h3>
              <p className="mt-3 font-body text-sm text-[#FDF7EA]/85 max-w-xl">
                Age brackets, product safety, parent responsibilities, booth logistics,
                and money-handling ground rules. Drop the finished PDF into this slot
                and the button lights up — no code changes needed.
              </p>
            </div>
            {GUIDELINES_PDF_URL ? (
              <a
                href={GUIDELINES_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[color:var(--ed-terracotta)] hover:bg-[color:var(--ed-terracotta)]/90 text-white font-body text-sm transition-colors flex-shrink-0"
                data-testid="ams-kids-guidelines-download"
              >
                <Download className="w-4 h-4" />
                Download PDF ({GUIDELINES_YEAR})
              </a>
            ) : (
              <span
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#FDF7EA]/15 text-[#FDF7EA]/70 font-body text-sm cursor-not-allowed flex-shrink-0"
                data-testid="ams-kids-guidelines-download-pending"
              >
                <Download className="w-4 h-4" />
                PDF coming soon
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply-form" className="max-w-3xl mx-auto px-6 pb-32">
        <div className="card-warm p-8 md:p-10" data-testid="ams-kids-form-card">
          <div className="flex items-center mb-4">
            <span className="rule" />
            <span className="kicker">Apply</span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl leading-tight text-[color:var(--ed-forest-dark)]">
            Sign Up Here!
          </h2>
          <p className="mt-3 font-body text-[color:var(--ed-muted)]">
            A parent or guardian fills this information out. We&apos;ll follow up with an email to invite you to our next event!
          </p>

          {submitted ? (
            <div
              className="mt-8 p-6 rounded-2xl bg-[color:var(--ed-gold)]/10 border border-[color:var(--ed-gold)]/40"
              data-testid="ams-kids-success"
            >
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[color:var(--ed-gold)]" />
                <h3 className="font-editorial text-2xl text-[color:var(--ed-forest-dark)]">
                  Application received.
                </h3>
              </div>
              <p className="mt-2 font-body text-sm text-[color:var(--ed-muted)]">
                We&apos;ll be in touch about the next Young CEOs cohort, prep session,
                and booth dates.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ams-kids-form">
              <Field
                label="Parent / guardian name *"
                value={form.parent_name}
                onChange={onChange("parent_name")}
                testid="ams-kids-input-parent"
                required
              />
              <Field
                label="Young CEO's name *"
                value={form.kid_name}
                onChange={onChange("kid_name")}
                testid="ams-kids-input-kid"
                required
              />
              <Field
                label="Email *"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                testid="ams-kids-input-email"
                required
              />
              <Field
                label="Phone"
                type="tel"
                value={form.phone}
                onChange={onChange("phone")}
                testid="ams-kids-input-phone"
              />
              <Field
                label="Young CEO's age (8–17)"
                type="number"
                value={form.age}
                onChange={onChange("age")}
                testid="ams-kids-input-age"
              />
              <div className="md:col-span-2">
                <label className="block text-xs font-body uppercase tracking-widest text-[color:var(--ed-muted)] mb-2">
                  What do they make? *
                </label>
                <textarea
                  className="admin-input w-full min-h-[140px] resize-y"
                  placeholder="Inventions, original artwork, jewelry, candles, cookies, 3D-printed characters — describe their product line."
                  value={form.product_description}
                  onChange={onChange("product_description")}
                  required
                  data-testid="ams-kids-input-product"
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
                <p className="font-body text-xs text-[color:var(--ed-muted)] max-w-md">
                  A parent or guardian must be present at every AMS Kids event.
                  Full ground rules live in the {GUIDELINES_YEAR} guidelines PDF above.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="ams-kids-submit"
                >
                  {submitting ? "Sending…" : "Apply"}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-body uppercase tracking-widest text-[color:var(--ed-muted)] mb-2">
        {label}
      </label>
      <input
        type={type}
        className="admin-input w-full"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
      />
    </div>
  );
}
